import React, { useCallback, useEffect, useState } from 'react';
import { termsApi, clientsApi } from '../services/api';
import type { TermRow, Client } from '../services/api';
import { Button } from '../ds/components/Button';
import { Icon } from '../ds/components/Icon';
import { InputField } from '../ds/InputField';
import { useCeebAcceptPolling, mailLabel } from '../hooks/useCeebAcceptPolling';
import CeebAcceptModal, { CeebAcceptTarget } from './CeebAcceptModal';
import { useAuthOptional } from '../contexts/AuthContext';
import { romanTerm } from '../utils/attempts';
import { saveBlob } from '../utils/download';
import { LIFECYCLE_META } from './lifecycleMeta';

/**
 * TermsTab — „Terminy" (H1 + H2, §17 notatki; decyzje właściciela 2026-08-04).
 *
 * Pipeline odpowiada na pytanie „co z tym budynkiem", a ten widok na „co mnie czeka w tym
 * tygodniu": JEDEN wiersz = JEDEN termin, wszystkie budynki razem, po dacie rosnąco.
 *
 * ⚠️ Numeru terminu NIE liczymy tutaj — `termNumber` przychodzi z backendu (kanon
 * `utils/inspectionTerms`, numeracja slotowa). Front zamienia go tylko na rzymski.
 *
 * H2: zielony „Akceptuj w CEEB" w wierszu — warunki 1:1 z PipelineView (superadmin +
 * `ceebStatus === 'Wprowadzony'` + jeszcze nie zaakceptowany). Akcja jest NIEODWRACALNA
 * w CEEB, więc idzie przez CeebAcceptModal — wspólny z PipelineView: pyta serwer o bramkę
 * (2026-10-09: lokale z terenu poza CEEB) zanim pozwoli kliknąć.
 */

const PAGE_SIZE = 50;

/** 'YYYY-MM-DD' → 'DD.MM.YYYY' (konwencja pl-PL bez godzin). */
function formatDay(d: string): string {
  const p = d.split('-');
  return p.length === 3 ? `${p[2]}.${p[1]}.${p[0]}` : d;
}

const cellStyle: React.CSSProperties = {
  padding: 'var(--space-3) var(--space-4)',
  borderBottom: 'var(--border-width) solid var(--border)',
  fontSize: 'var(--text-label)',
  color: 'var(--text-primary)',
  verticalAlign: 'middle',
};

const headStyle: React.CSSProperties = {
  ...cellStyle,
  background: 'var(--surface-fill)',
  fontWeight: 'var(--weight-semibold)',
  color: 'var(--text-secondary)',
  textAlign: 'left',
  whiteSpace: 'nowrap',
};

const TermsTab: React.FC = () => {
  const auth = useAuthOptional();
  const isSuperadmin = auth?.user?.role === 'superadmin';

  const [rows, setRows] = useState<TermRow[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [clientId, setClientId] = useState('');
  const [clients, setClients] = useState<Client[]>([]);

  const [acceptTarget, setAcceptTarget] = useState<CeebAcceptTarget | null>(null);
  const [notice, setNotice] = useState<{ kind: 'ok' | 'error'; text: string } | null>(null);
  // PROMPT 5: grafik bieżącego filtra do Excela (backend wymaga co najmniej jednego kryterium).
  const [exporting, setExporting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await termsApi.list({
        dateFrom: dateFrom || undefined,
        dateTo: dateTo || undefined,
        clientId: clientId || undefined,
        page,
        pageSize: PAGE_SIZE,
      });
      setRows(res.data);
      setTotal(res.total);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Nie udało się pobrać terminów');
      setRows([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [dateFrom, dateTo, clientId, page]);

  useEffect(() => { load(); }, [load]);

  // Lista zarządców do filtra — błąd degraduje do samego „— wszyscy —", nie psuje widoku.
  useEffect(() => {
    let alive = true;
    clientsApi.getAll()
      .then((cs) => { if (alive) setClients(cs); })
      .catch(() => { if (alive) setClients([]); });
    return () => { alive = false; };
  }, []);

  // Każda zmiana filtra wraca na stronę 1 — inaczej można wylądować na pustej stronie 7.
  const setFilter = (fn: () => void) => { fn(); setPage(1); };

  const hasFilter = Boolean(dateFrom || dateTo || clientId);
  const exportXlsx = async () => {
    if (!hasFilter || exporting) return;
    setExporting(true);
    setNotice(null);
    try {
      const { blob, filename } = await termsApi.exportXlsx({
        dateFrom: dateFrom || undefined,
        dateTo: dateTo || undefined,
        clientId: clientId || undefined,
      });
      saveBlob(blob, filename);
    } catch (e) {
      setNotice({ kind: 'error', text: e instanceof Error ? e.message : 'Nie udało się pobrać grafiku' });
    } finally {
      setExporting(false);
    }
  };

  // Wynik akceptacji → baner; lista odświeżana po każdym wyniku (status CEEB w wierszu).
  const acceptJob = useCeebAcceptPolling((o) => {
    if (o.status === 'done') {
      const mail = mailLabel(o.mail);
      setNotice({ kind: 'ok', text: `Protokół zaakceptowany w CEEB.${mail ? ` ${mail}.` : ''}` });
    } else {
      setNotice({ kind: 'error', text: `Akceptacja nie powiodła się: ${o.error || 'nieznany błąd'}` });
    }
    void load();
  });

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-3)', marginBottom: 'var(--space-2)' }}>
        <Icon name="calendar" size={26} color="var(--action-primary)" />
        <h2 style={{ fontSize: 'var(--text-h2, 22px)', fontWeight: 700, color: 'var(--text-primary)' }}>
          Terminy
        </h2>
      </div>
      <p style={{ color: 'var(--text-secondary)', marginBottom: 'var(--space-5)', maxWidth: 760 }}>
        Wszystkie terminy przeglądów w jednym miejscu, po dacie rosnąco. Jeden wiersz to jeden
        termin — budynek z trzema terminami pojawi się trzy razy.
      </p>

      {/* Filtry */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--space-4)', alignItems: 'flex-end', marginBottom: 'var(--space-4)' }}>
        <InputField
          label="Od dnia"
          type="date"
          value={dateFrom}
          onChange={(e) => setFilter(() => setDateFrom(e.target.value))}
          containerStyle={{ maxWidth: 190 }}
        />
        <InputField
          label="Do dnia"
          type="date"
          value={dateTo}
          onChange={(e) => setFilter(() => setDateTo(e.target.value))}
          containerStyle={{ maxWidth: 190 }}
        />
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-2)' }}>
          <label
            htmlFor="terms-client"
            style={{
              fontFamily: 'var(--font-sans)',
              fontSize: 'var(--text-label)',
              fontWeight: 'var(--weight-semibold)',
              color: 'var(--text-secondary)',
            }}
          >
            Zarządca
          </label>
          {/* Natywny select — brak DS Select (wzorzec AddBuildingModal / PipelineView). */}
          <select
            id="terms-client"
            value={clientId}
            onChange={(e) => setFilter(() => setClientId(e.target.value))}
            style={{
              minHeight: 'var(--touch-min)',
              maxWidth: 260,
              padding: '0 var(--space-4)',
              fontFamily: 'var(--font-sans)',
              fontSize: 'var(--text-body)',
              color: 'var(--text-primary)',
              background: 'var(--surface-card)',
              border: 'var(--border-width-strong) solid var(--border-strong)',
              borderRadius: 'var(--radius-md)',
              cursor: 'pointer',
            }}
          >
            <option value="">— wszyscy —</option>
            {clients.map((c) => (
              <option key={c.id} value={c.id}>{c.name}</option>
            ))}
          </select>
        </div>
        {hasFilter && (
          <Button
            variant="ghost"
            onClick={() => setFilter(() => { setDateFrom(''); setDateTo(''); setClientId(''); })}
            style={{ minHeight: 'var(--touch-min)', padding: '0 var(--space-3)' }}
          >
            Wyczyść filtry
          </Button>
        )}
        <Button
          variant="secondary"
          icon="download"
          onClick={exportXlsx}
          disabled={!hasFilter || exporting}
          title={hasFilter
            ? 'Grafik terminów z bieżącego filtra jako plik Excela (jeden wiersz = jeden budynek, I i II termin w osobnych kolumnach)'
            : 'Ustaw zakres dat albo zarządcę — grafik „ze wszystkiego” to nie grafik'}
          style={{ minHeight: 'var(--touch-min)' }}
        >
          {exporting ? 'Generowanie…' : 'Pobierz do Excela'}
        </Button>
      </div>

      {notice && (
        <div
          role="alert"
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 'var(--space-3)',
            padding: 'var(--space-3) var(--space-4)',
            marginBottom: 'var(--space-4)',
            borderRadius: 'var(--radius-md)',
            background: notice.kind === 'ok' ? 'var(--status-ok-bg)' : 'var(--status-deny-bg)',
            border: `var(--border-width) solid ${notice.kind === 'ok' ? 'var(--status-ok)' : 'var(--status-deny-bar)'}`,
            color: notice.kind === 'ok' ? 'var(--status-ok)' : 'var(--status-deny)',
            fontSize: 'var(--text-label)',
          }}
        >
          <Icon name={notice.kind === 'ok' ? 'check' : 'triangle-alert'} size={18} />
          <span style={{ flex: 1 }}>{notice.text}</span>
        </div>
      )}

      {loading && <p style={{ color: 'var(--text-muted)' }}>Ładowanie terminów…</p>}
      {error && (
        <p role="alert" style={{ color: 'var(--status-deny)' }}>
          {error} —{' '}
          <button
            type="button"
            onClick={load}
            style={{ textDecoration: 'underline', cursor: 'pointer', background: 'none', border: 'none', color: 'inherit' }}
          >
            spróbuj ponownie
          </button>
        </p>
      )}

      {!loading && !error && rows.length === 0 && (
        <p style={{ color: 'var(--text-muted)' }}>
          {dateFrom || dateTo || clientId
            ? 'Brak terminów spełniających filtry.'
            : 'Nie ma jeszcze żadnych zaplanowanych terminów.'}
        </p>
      )}

      {!loading && !error && rows.length > 0 && (
        <>
          <p style={{ color: 'var(--text-secondary)', marginBottom: 'var(--space-3)' }}>
            Znaleziono {total} {total === 1 ? 'termin' : 'terminów'}
          </p>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', background: 'var(--surface-card)' }}>
              <thead>
                <tr>
                  <th style={headStyle}>Data</th>
                  <th style={headStyle}>Termin</th>
                  <th style={headStyle}>Adres</th>
                  <th style={headStyle}>Zarządca</th>
                  <th style={headStyle}>Etap</th>
                  <th style={headStyle}>Status CEEB</th>
                  <th style={headStyle} />
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => {
                  const meta = LIFECYCLE_META[r.lifecycleState] || LIFECYCLE_META.NOWY;
                  const canAccept = isSuperadmin && r.ceebStatus === 'Wprowadzony' && !r.ceebAcceptedAt;
                  const busy = !!acceptJob.running[r.buildingId];
                  return (
                    <tr key={`${r.inspectionId}-${r.termNumber}`}>
                      <td style={{ ...cellStyle, whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }}>
                        {formatDay(r.termDate)}
                      </td>
                      <td style={{ ...cellStyle, whiteSpace: 'nowrap', fontWeight: 'var(--weight-semibold)' }}>
                        {romanTerm(r.termNumber) || r.termNumber} termin
                      </td>
                      <td style={cellStyle}>{r.address}</td>
                      <td style={{ ...cellStyle, color: r.clientName ? 'var(--text-primary)' : 'var(--text-muted)' }}>
                        {r.clientName || '—'}
                      </td>
                      <td style={cellStyle}>
                        <span
                          style={{
                            display: 'inline-flex',
                            padding: '4px 10px',
                            borderRadius: 'var(--radius-pill)',
                            background: meta.bg,
                            color: meta.fg,
                            fontSize: 'var(--text-label)',
                            fontWeight: 'var(--weight-semibold)',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {meta.label}
                        </span>
                      </td>
                      <td style={{ ...cellStyle, color: r.ceebStatus ? 'var(--text-primary)' : 'var(--text-muted)' }}>
                        {r.ceebStatus || '—'}
                        {r.ceebCaseNumber && (
                          <span style={{ color: 'var(--text-muted)' }}> · {r.ceebCaseNumber}</span>
                        )}
                      </td>
                      <td style={{ ...cellStyle, textAlign: 'right' }}>
                        {canAccept && (
                          <Button
                            variant="primary"
                            icon="check"
                            disabled={busy}
                            aria-label={`Akceptuj w CEEB — ${r.address}`}
                            style={{
                              minHeight: 38,
                              padding: '0 var(--space-3)',
                              fontSize: 'var(--text-caption)',
                              background: 'var(--status-ok, #2e7d32)',
                              borderColor: 'var(--status-ok, #2e7d32)',
                            }}
                            onClick={() => { setNotice(null); setAcceptTarget({ buildingId: r.buildingId, address: r.address }); }}
                          >
                            {busy ? 'Akceptowanie…' : 'Akceptuj w CEEB'}
                          </Button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {totalPages > 1 && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-3)', marginTop: 'var(--space-4)' }}>
              <Button
                variant="secondary"
                disabled={page <= 1}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                style={{ minHeight: 'var(--touch-min)', padding: '0 var(--space-4)' }}
              >
                Poprzednia
              </Button>
              <span style={{ color: 'var(--text-secondary)', fontSize: 'var(--text-label)' }}>
                Strona {page} z {totalPages}
              </span>
              <Button
                variant="secondary"
                disabled={page >= totalPages}
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                style={{ minHeight: 'var(--touch-min)', padding: '0 var(--space-4)' }}
              >
                Następna
              </Button>
            </div>
          )}
        </>
      )}

      {/* Akceptacja jest NIEODWRACALNA w CEEB — potwierdzenie z bramką serwera. */}
      <CeebAcceptModal
        target={acceptTarget}
        onCancel={() => setAcceptTarget(null)}
        onConfirm={(buildingId, opts) => { setAcceptTarget(null); void acceptJob.start(buildingId, opts); }}
      />
    </div>
  );
};

export default TermsTab;
